// Helpers for POSTing to the SecureShare API through CloudFront.
//
// Why x-amz-content-sha256: CloudFront OAC signs origin requests to the
// Lambda Function URL with SigV4. For POST/PUT the SigV4 canonical request
// includes a body hash; CloudFront reads x-amz-content-sha256 from the viewer
// request and folds that value into the signature. The Lambda Function URL
// service recomputes the hash from the actual body on the origin side and
// compares. If the header is missing or its value doesn't match the bytes on
// the wire, the request fails with InvalidSignatureException before Flask
// ever sees it.
//
// Scope: only call these helpers for /api/* endpoints that go through our
// CloudFront + OAC path. Direct-to-S3 presigned uploads and any MSAL/OIDC
// token endpoints have their own signing and must NOT receive this header.

async function sha256Hex(bytes) {
    const hash = await crypto.subtle.digest('SHA-256', bytes);
    return Array.from(new Uint8Array(hash))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

function buildHeaders({ contentType, bodyHash, token, apiKey }) {
    const headers = {
        'x-amz-content-sha256': bodyHash,
    };
    if (contentType) headers['Content-Type'] = contentType;
    if (apiKey) headers['X-Api-Key'] = apiKey;
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
}

// JSON POST. Use for all current /api/* endpoints — they all take JSON bodies
// (including the empty-object `{}` case, which hashes deterministically).
export async function apiPost(url, body, { token, apiKey } = {}) {
    const jsonBody = JSON.stringify(body ?? {});
    const bodyBytes = new TextEncoder().encode(jsonBody);
    return fetch(url, {
        method: 'POST',
        cache: 'no-cache',
        headers: buildHeaders({
            contentType: 'application/json',
            bodyHash: await sha256Hex(bodyBytes),
            token,
            apiKey,
        }),
        body: jsonBody,
    });
}

// Multipart/FormData POST. Currently unused — file uploads in this app go
// direct to S3 via a presigned POST, not through /api/*. Kept here so that if
// we ever route an upload through /api/* (e.g. if we move off presigned URLs)
// the hashing is already correct.
//
// Why the dance: the browser picks a random multipart boundary at Request
// construction time and bakes it into the Content-Type header. To make the
// x-amz-content-sha256 header match the bytes that actually go on the wire
// we must (a) build a throwaway Request to freeze a boundary and materialize
// the bytes, (b) hash those exact bytes, (c) re-send using the same bytes
// plus the Content-Type that encodes the same boundary. Passing the raw
// FormData straight to a second fetch() would pick a new boundary and
// invalidate the hash.
export async function apiPostForm(url, formData, { token, apiKey } = {}) {
    const probe = new Request(url, { method: 'POST', body: formData });
    const contentType = probe.headers.get('Content-Type');
    const bytes = await probe.arrayBuffer();
    return fetch(url, {
        method: 'POST',
        cache: 'no-cache',
        headers: buildHeaders({
            contentType,
            bodyHash: await sha256Hex(bytes),
            token,
            apiKey,
        }),
        body: bytes,
    });
}
